import { useCallback, useEffect, useRef, useState } from 'react';
import { getRecommendedMarkets, getMarketContexts } from '../utils/api';
import { mapApiItems } from '../utils/marketUtils';

export function useMarketData(activeSection, pushToast) {
  const [activeMarketIndex, setActiveMarketIndex] = useState(0);
  const [marketData, setMarketData] = useState([]);
  const [page, setPage] = useState(0);
  const [hasNext, setHasNext] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const prefetchLockRef = useRef(false);
  const fetchedPagesRef = useRef(new Set());
  const lastFetchIndexRef = useRef(-1);
  const pageRef = useRef(page);
  const hasNextRef = useRef(hasNext);
  const marketDataRef = useRef(marketData);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    hasNextRef.current = hasNext;
  }, [hasNext]);

  useEffect(() => {
    marketDataRef.current = marketData;
  }, [marketData]);

  const activeMarket = marketData[activeMarketIndex] || marketData[0];

  // 根据一批 slug 拉取上下文，并写回到 marketData
  const fetchAndAttachContexts = useCallback(async (items) => {
    try {
      const slugs = (items || []).map((it) => it.slug || it.id).filter(Boolean);
      if (!slugs.length) return;

      const res = await getMarketContexts(slugs);
      const ctxMap = new Map();
      if (Array.isArray(res?.data)) {
        res.data.forEach((ctx) => {
          if (ctx.slug) ctxMap.set(ctx.slug, ctx);
        });
      }

      setMarketData((prev) =>
        prev.map((m) => {
          const ctx = ctxMap.get(m.id);
          if (!ctx) return m;
          return {
            ...m,
            aiHighlights: Array.isArray(ctx.highlights) ? ctx.highlights : [],
            aiNarrative: Array.isArray(ctx.narrative) ? ctx.narrative : []
          };
        })
      );
    } catch (e) {
      console.error('[fetchAndAttachContexts] 失败：', e);
    }
  }, []);

  // 市场切换
  const cycleMarket = useCallback((direction) => {
    setActiveMarketIndex((prev) => {
      const currentLength = marketDataRef.current.length;
      const currentHasNext = hasNextRef.current;

      if (direction === 'next') {
        const nextIndex = prev + 1;
        console.log(`[滑动] next: 当前索引=${prev}, 下一个=${nextIndex}, 列表长度=${currentLength}, hasNext=${currentHasNext}`);

        if (nextIndex >= currentLength) {
          if (!currentHasNext) {
            pushToast('已到达推荐列表末尾，暂无更多市场', 'info');
          } else if (!prefetchLockRef.current && !isLoading) {
            const nextPage = pageRef.current + 1;
            if (!fetchedPagesRef.current.has(nextPage)) {
              (async () => {
                try {
                  prefetchLockRef.current = true;
                  setIsLoading(true);
                  console.log(`[边界预取] 触发第 ${nextPage} 页预取，currentIndex=${prev}, length=${currentLength}`);
                  const res = await getRecommendedMarkets(nextPage, 5);
                  const items = res?.data?.items || [];
                  const mapped = mapApiItems(items);
                  const nextFlag = !!res?.data?.pagination?.has_next;
                  if (mapped.length > 0) {
                    setMarketData((p) => [...p, ...mapped]);
                    setPage(nextPage);
                    setHasNext(nextFlag);
                    fetchedPagesRef.current.add(nextPage);
                    console.log(`[边界预取] 成功追加 ${mapped.length} 条数据`);
                    fetchAndAttachContexts(items);
                  } else {
                    setHasNext(false);
                    console.log('[边界预取] 返回空数据，标记无更多');
                  }
                } catch (e) {
                  console.error('[边界预取] 失败：', e);
                } finally {
                  prefetchLockRef.current = false;
                  setIsLoading(false);
                }
              })();
            }
          }
          return prev;
        }
        return nextIndex;
      }
      if (direction === 'prev') {
        const prevIndex = prev - 1;
        console.log(`[滑动] prev: 当前索引=${prev}, 上一个=${prevIndex}`);
        return prevIndex < 0 ? 0 : prevIndex;
      }
      return prev;
    });
  }, [isLoading, pushToast, fetchAndAttachContexts]);

  // 初始加载推荐页数据
  useEffect(() => {
    if (activeSection !== 'recommend') return;
    if (marketData.length > 0) return; // 已有数据

    (async () => {
      try {
        setIsLoading(true);
        console.log('[推荐页] 加载首页数据');
        const res = await getRecommendedMarkets(1, 5);
        const items = res?.data?.items || [];
        const mapped = mapApiItems(items);
        const nextFlag = !!res?.data?.pagination?.has_next;
        setMarketData(mapped);
        setPage(1);
        setHasNext(nextFlag);
        fetchedPagesRef.current.add(1);
        console.log(`[推荐页] 首页加载成功，${mapped.length} 条数据`);
        fetchAndAttachContexts(items);
      } catch (e) {
        console.error('[推荐页] 加载失败：', e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [activeSection, marketData.length, fetchAndAttachContexts]);

  // 预取逻辑：当索引接近末尾时预取下一页
  useEffect(() => {
    if (activeSection !== 'recommend') return;
    if (marketData.length === 0) return;
    if (activeMarketIndex === lastFetchIndexRef.current) return;

    lastFetchIndexRef.current = activeMarketIndex;
    const threshold = Math.max(2, Math.floor(marketData.length * 0.7));

    if (activeMarketIndex >= threshold && hasNext && !prefetchLockRef.current && !isLoading) {
      const nextPage = page + 1;
      if (!fetchedPagesRef.current.has(nextPage)) {
        (async () => {
          try {
            prefetchLockRef.current = true;
            setIsLoading(true);
            console.log(`[预取] 触发第 ${nextPage} 页预取，索引=${activeMarketIndex}, 阈值=${threshold}`);
            const res = await getRecommendedMarkets(nextPage, 5);
            const items = res?.data?.items || [];
            const mapped = mapApiItems(items);
            const nextFlag = !!res?.data?.pagination?.has_next;
            if (mapped.length > 0) {
              setMarketData((prev) => [...prev, ...mapped]);
              setPage(nextPage);
              setHasNext(nextFlag);
              fetchedPagesRef.current.add(nextPage);
              console.log(`[预取] 成功追加 ${mapped.length} 条数据`);
              fetchAndAttachContexts(items);
            } else {
              setHasNext(false);
              console.log('[预取] 返回空数据，标记无更多');
            }
          } catch (e) {
            console.error('[预取] 失败：', e);
          } finally {
            prefetchLockRef.current = false;
            setIsLoading(false);
          }
        })();
      }
    }
  }, [activeSection, activeMarketIndex, marketData.length, hasNext, page, isLoading, fetchAndAttachContexts]);

  // 键盘导航
  useEffect(() => {
    if (activeSection !== 'recommend' && activeSection !== 'favorites') return;

    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        cycleMarket('prev');
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        cycleMarket('next');
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeSection, cycleMarket]);

  return {
    activeMarket,
    activeMarketIndex,
    marketData,
    page,
    hasNext,
    isLoading,
    cycleMarket,
    setActiveMarketIndex,
    setMarketData,
    setPage,
    setHasNext,
    // 导出 refs 供外部缓存使用
    marketDataRef,
    pageRef,
    hasNextRef
  };
}
